import {
  CONNECTOR_FAMILY_CODE_FLAT_FLIP,
  CONNECTOR_FAMILY_CODE_GAP,
  CONNECTOR_FAMILY_CODE_KKT_A4_AISI316,
  CONNECTOR_FAMILY_CODE_KKT_COLOR,
  CONNECTOR_FAMILY_CODE_SNAP,
  CONNECTOR_FAMILY_CODE_TERRALOCK,
  CONNECTOR_FAMILY_CODE_TERRALOCK_A2,
  CONNECTOR_FAMILY_CODE_TERRALOCK_PP,
  CONNECTOR_FAMILY_CODE_TVM,
} from "../../../http/deckDesigner/types/connectorFamilies";
import type { ScrewForSettings } from "../../../http/deckDesigner/types/screws";
import type { SecondaryItem } from "../../../http/deckDesigner/types/secondaryItems";
import { SUBSTRUCTURE_FAMILY_CODE_ALUTERRACE } from "../../../http/deckDesigner/types/substructureFamilies";
import {
  SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM,
  SUBSTRUCTURE_MATERIAL_CODE_TIMBER,
  SUBSTRUCTURE_MATERIAL_CODE_WPC,
  type SubstructureMaterialForSettings,
} from "../../../http/deckDesigner/types/substructureMaterials";
import type { SubstructureForSettings } from "../../../http/deckDesigner/types/substructures";
import { isFiniteNumber } from "../../../utils/numberFormat";

// Screw-length formulas don't differ between timber and WPC, only between wood and aluminium,
// so both real material codes collapse into this single internal "wood" kind.
const MATERIAL_KIND_TIMBER_WPC = "timber-wpc" as const;

type MaterialKind = typeof SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM | typeof MATERIAL_KIND_TIMBER_WPC;
type ConnectorScrewLengthCalculator = (input: MinimumConnectorScrewLengthInput) => number | undefined;
type MinimumConnectorScrewLengthCalculator = ConnectorScrewLengthCalculator;
type MaximumConnectorScrewLengthCalculator = ConnectorScrewLengthCalculator;

export type MinimumConnectorScrewLengthInput = {
  connectorFamilyCode: string | null | undefined;
  substructureMaterial: SubstructureMaterialForSettings | null | undefined;
  boardThickness: number | null | undefined;
  groovingHeight: number | null | undefined;
  groovingThickness: number | null | undefined;
  connectorMinGroovingHeight: number | null | undefined;
  substructure: SubstructureForSettings | null | undefined;
  secondaryItem: SecondaryItem | null | undefined;
  screw: ScrewForSettings | null | undefined;
};

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

// Mirrors Excel's MROUND(value, multiple): rounds to the nearest multiple before the min/max clamp is applied,
// matching the reference spreadsheet exactly (e.g. MROUND(31.5, 5) = 30).
export const mround = (value: number, multiple: number): number => Math.round(value / multiple) * multiple;

const getMaterialKind = (material: SubstructureMaterialForSettings | null | undefined): MaterialKind | undefined => {
  if (!material) {
    return undefined;
  }

  if (material.code === SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM) {
    return SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM;
  }

  if (material.code === SUBSTRUCTURE_MATERIAL_CODE_TIMBER || material.code === SUBSTRUCTURE_MATERIAL_CODE_WPC) {
    return MATERIAL_KIND_TIMBER_WPC;
  }

  return undefined;
};

const getAluterraceAllowance = (substructure: SubstructureForSettings | null | undefined): number | undefined => {
  if (substructure?.substructureFamily?.code !== SUBSTRUCTURE_FAMILY_CODE_ALUTERRACE) {
    return undefined;
  }

  if (substructure?.height === 30) {
    return 20;
  }

  if (substructure?.height === 50) {
    return 25;
  }

  return undefined;
};

const getSecondaryItemThickness = (secondaryItem: SecondaryItem | null | undefined): number | undefined => {
  if (!secondaryItem) {
    return 0;
  }

  return isFiniteNumber(secondaryItem.thickness) ? secondaryItem.thickness : undefined;
};

const getScrewHeadHeight = (screw: ScrewForSettings | null | undefined): number | undefined =>
  isFiniteNumber(screw?.screwHeadHeight) ? screw.screwHeadHeight : undefined;

const getScrewPenetration = (
  screw: ScrewForSettings | null | undefined,
  substructure: SubstructureForSettings | null | undefined,
): number | undefined => {
  if (!isFiniteNumber(screw?.screwDiameter) || !isFiniteNumber(substructure?.height)) {
    return undefined;
  }

  return Math.min(4 * screw.screwDiameter, substructure.height);
};

const getWoodDeckInputs = (
  input: MinimumConnectorScrewLengthInput,
): { screwHeadHeight: number; secondaryItemThickness: number; screwPenetration: number } | undefined => {
  const screwHeadHeight = getScrewHeadHeight(input.screw);
  const secondaryItemThickness = getSecondaryItemThickness(input.secondaryItem);
  const screwPenetration = getScrewPenetration(input.screw, input.substructure);

  if (
    !isFiniteNumber(screwHeadHeight) ||
    !isFiniteNumber(secondaryItemThickness) ||
    !isFiniteNumber(screwPenetration)
  ) {
    return undefined;
  }

  return { screwHeadHeight, secondaryItemThickness, screwPenetration };
};

const getKktInputs = (
  input: MinimumConnectorScrewLengthInput,
): { secondaryItemThickness: number; screwPenetration: number } | undefined => {
  const secondaryItemThickness = getSecondaryItemThickness(input.secondaryItem);
  const screwPenetration = getScrewPenetration(input.screw, input.substructure);

  if (!isFiniteNumber(secondaryItemThickness) || !isFiniteNumber(screwPenetration)) {
    return undefined;
  }

  return { secondaryItemThickness, screwPenetration };
};

export const getFlatFlipMinimumConnectorScrewLength: MinimumConnectorScrewLengthCalculator = (input) => {
  const materialKind = getMaterialKind(input.substructureMaterial);

  if (!materialKind || !isFiniteNumber(input.boardThickness)) {
    return undefined;
  }

  if (materialKind === SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM) {
    const aluterraceAllowance = getAluterraceAllowance(input.substructure);
    return isFiniteNumber(aluterraceAllowance)
      ? mround(input.boardThickness / 2 + 4 + aluterraceAllowance, 10)
      : undefined;
  }

  const inputs = getWoodDeckInputs(input);
  return inputs
    ? clamp(
        mround(
          input.boardThickness / 2 +
            inputs.screwHeadHeight +
            4 +
            inputs.secondaryItemThickness +
            inputs.screwPenetration,
          10,
        ),
        40,
        70,
      )
    : undefined;
};

// TVM's minimum length is governed by substructure penetration plus the groove clearance left above
// the connector's own minimum grooving height (mirrors the maximum formula below) - applies to both
// timber/WPC and aluminium substructures.
export const getTvmMinimumConnectorScrewLength: MinimumConnectorScrewLengthCalculator = (input) => {
  if (!getMaterialKind(input.substructureMaterial)) {
    return undefined;
  }

  const screwPenetration = getScrewPenetration(input.screw, input.substructure);
  const secondaryItemThickness = getSecondaryItemThickness(input.secondaryItem);
  const screwHeadHeight = getScrewHeadHeight(input.screw);

  if (
    !isFiniteNumber(screwPenetration) ||
    !isFiniteNumber(secondaryItemThickness) ||
    !isFiniteNumber(input.groovingHeight) ||
    !isFiniteNumber(input.connectorMinGroovingHeight) ||
    !isFiniteNumber(screwHeadHeight)
  ) {
    return undefined;
  }

  return (
    screwPenetration +
    secondaryItemThickness +
    (input.groovingHeight - input.connectorMinGroovingHeight) +
    screwHeadHeight
  );
};

export const getGapMinimumConnectorScrewLength: MinimumConnectorScrewLengthCalculator = (input) => {
  const materialKind = getMaterialKind(input.substructureMaterial);

  if (!materialKind) {
    return undefined;
  }

  if (materialKind === SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM) {
    return 25;
  }

  const screwPenetration = getScrewPenetration(input.screw, input.substructure);
  return isFiniteNumber(screwPenetration) ? clamp(mround(1.5 + screwPenetration, 5), 25, 35) : undefined;
};

export const getSnapMinimumConnectorScrewLength: MinimumConnectorScrewLengthCalculator = (input) => {
  const materialKind = getMaterialKind(input.substructureMaterial);

  if (!materialKind) {
    return undefined;
  }

  if (materialKind === SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM) {
    const aluterraceAllowance = getAluterraceAllowance(input.substructure);
    return isFiniteNumber(aluterraceAllowance) ? Math.max(mround(4 + aluterraceAllowance, 10), 40) : undefined;
  }

  const screwHeadHeight = getScrewHeadHeight(input.screw);
  const screwPenetration = getScrewPenetration(input.screw, input.substructure);
  const secondaryItemThickness = getSecondaryItemThickness(input.secondaryItem);

  if (
    !isFiniteNumber(screwHeadHeight) ||
    !isFiniteNumber(screwPenetration) ||
    !isFiniteNumber(secondaryItemThickness) ||
    !isFiniteNumber(input.groovingHeight) ||
    !isFiniteNumber(input.groovingThickness)
  ) {
    return undefined;
  }

  return screwPenetration + input.groovingHeight + input.groovingThickness + screwHeadHeight + secondaryItemThickness;
};

export const getTerralockMinimumConnectorScrewLength: MinimumConnectorScrewLengthCalculator = (input) => {
  const screwHeadHeight = getScrewHeadHeight(input.screw);

  if (!isFiniteNumber(screwHeadHeight) || !isFiniteNumber(input.screw?.screwDiameter)) {
    return undefined;
  }

  return mround(screwHeadHeight + 8 + 4 * input.screw.screwDiameter, 5);
};

const getKktMinimumConnectorScrewLength: MinimumConnectorScrewLengthCalculator = (input) => {
  const materialKind = getMaterialKind(input.substructureMaterial);

  if (!materialKind || !isFiniteNumber(input.boardThickness)) {
    return undefined;
  }

  const inputs = getKktInputs(input);

  if (!inputs) {
    return undefined;
  }

  const minimumLength = mround(input.boardThickness + inputs.secondaryItemThickness + inputs.screwPenetration, 10);

  return materialKind === SUBSTRUCTURE_MATERIAL_CODE_ALUMINIUM
    ? clamp(minimumLength, 40, 50)
    : clamp(minimumLength, 50, 70);
};

export const getKktColorMinimumConnectorScrewLength: MinimumConnectorScrewLengthCalculator = (input) =>
  getKktMinimumConnectorScrewLength(input);

export const getKktA4Aisi316MinimumConnectorScrewLength: MinimumConnectorScrewLengthCalculator = (input) =>
  getKktMinimumConnectorScrewLength(input);

const minimumConnectorScrewLengthCalculators: Record<string, MinimumConnectorScrewLengthCalculator> = {
  [CONNECTOR_FAMILY_CODE_FLAT_FLIP]: getFlatFlipMinimumConnectorScrewLength,
  [CONNECTOR_FAMILY_CODE_TVM]: getTvmMinimumConnectorScrewLength,
  [CONNECTOR_FAMILY_CODE_GAP]: getGapMinimumConnectorScrewLength,
  [CONNECTOR_FAMILY_CODE_SNAP]: getSnapMinimumConnectorScrewLength,
  [CONNECTOR_FAMILY_CODE_TERRALOCK]: getTerralockMinimumConnectorScrewLength,
  [CONNECTOR_FAMILY_CODE_TERRALOCK_A2]: getTerralockMinimumConnectorScrewLength,
  [CONNECTOR_FAMILY_CODE_TERRALOCK_PP]: getTerralockMinimumConnectorScrewLength,
  [CONNECTOR_FAMILY_CODE_KKT_COLOR]: getKktColorMinimumConnectorScrewLength,
  [CONNECTOR_FAMILY_CODE_KKT_A4_AISI316]: getKktA4Aisi316MinimumConnectorScrewLength,
};

export const getMinimumConnectorScrewLength: MinimumConnectorScrewLengthCalculator = (input) => {
  if (!input.connectorFamilyCode) {
    return undefined;
  }

  return minimumConnectorScrewLengthCalculators[input.connectorFamilyCode]?.(input);
};

// Maximum allowed screw length so the tip does not pierce through the bottom face of the substructure.
// Mirrors the minimum formulas above, but replaces the capped penetration term (min(4 * diameter, height))
// with the full substructure height, and drops the practical-minimum clamp floor.
// Only defined for timber/WPC families: aluminium profiles and Terralock use a fixed engagement depth
// that does not scale with substructure.height, so there is no equivalent poke-through risk to cap.

// Shared by FLAT | FLIP and SNAP: the screw's maximum length is governed by the groove it seats in,
// not by board thickness or screw head - it must clear the grooving thickness and height, plus reach
// through the substructure and any secondary item without poking through the bottom face.
const getGroovingBasedMaximumConnectorScrewLength: MaximumConnectorScrewLengthCalculator = (input) => {
  if (getMaterialKind(input.substructureMaterial) !== MATERIAL_KIND_TIMBER_WPC) {
    return undefined;
  }

  const secondaryItemThickness = getSecondaryItemThickness(input.secondaryItem);
  const substructureHeight = input.substructure?.height;

  if (
    !isFiniteNumber(input.groovingThickness) ||
    !isFiniteNumber(input.groovingHeight) ||
    !isFiniteNumber(secondaryItemThickness) ||
    !isFiniteNumber(substructureHeight)
  ) {
    return undefined;
  }

  return input.groovingThickness + input.groovingHeight + substructureHeight + secondaryItemThickness;
};

export const getFlatFlipMaximumConnectorScrewLength: MaximumConnectorScrewLengthCalculator = (input) =>
  getGroovingBasedMaximumConnectorScrewLength(input);

// TVM's maximum length is governed by how much groove clearance is left above the connector's own
// minimum grooving height, not by board thickness/screw head like the other formulas here - applies
// to both timber/WPC and aluminium substructures.
export const getTvmMaximumConnectorScrewLength: MaximumConnectorScrewLengthCalculator = (input) => {
  if (!getMaterialKind(input.substructureMaterial)) {
    return undefined;
  }

  const secondaryItemThickness = getSecondaryItemThickness(input.secondaryItem);
  const substructureHeight = input.substructure?.height;

  if (
    !isFiniteNumber(input.groovingHeight) ||
    !isFiniteNumber(input.connectorMinGroovingHeight) ||
    !isFiniteNumber(secondaryItemThickness) ||
    !isFiniteNumber(substructureHeight)
  ) {
    return undefined;
  }

  return substructureHeight + secondaryItemThickness + (input.groovingHeight - input.connectorMinGroovingHeight);
};

export const getGapMaximumConnectorScrewLength: MaximumConnectorScrewLengthCalculator = (input) => {
  if (getMaterialKind(input.substructureMaterial) !== MATERIAL_KIND_TIMBER_WPC) {
    return undefined;
  }

  const substructureHeight = input.substructure?.height;

  return isFiniteNumber(substructureHeight) ? 1.5 + substructureHeight : undefined;
};

export const getSnapMaximumConnectorScrewLength: MaximumConnectorScrewLengthCalculator = (input) =>
  getGroovingBasedMaximumConnectorScrewLength(input);

const getKktMaximumConnectorScrewLength: MaximumConnectorScrewLengthCalculator = (input) => {
  if (getMaterialKind(input.substructureMaterial) !== MATERIAL_KIND_TIMBER_WPC) {
    return undefined;
  }

  const secondaryItemThickness = getSecondaryItemThickness(input.secondaryItem);
  const substructureHeight = input.substructure?.height;

  if (
    !isFiniteNumber(input.boardThickness) ||
    !isFiniteNumber(secondaryItemThickness) ||
    !isFiniteNumber(substructureHeight)
  ) {
    return undefined;
  }

  return input.boardThickness + secondaryItemThickness + substructureHeight;
};

export const getKktColorMaximumConnectorScrewLength: MaximumConnectorScrewLengthCalculator = (input) =>
  getKktMaximumConnectorScrewLength(input);

export const getKktA4Aisi316MaximumConnectorScrewLength: MaximumConnectorScrewLengthCalculator = (input) =>
  getKktMaximumConnectorScrewLength(input);

const maximumConnectorScrewLengthCalculators: Record<string, MaximumConnectorScrewLengthCalculator> = {
  [CONNECTOR_FAMILY_CODE_FLAT_FLIP]: getFlatFlipMaximumConnectorScrewLength,
  [CONNECTOR_FAMILY_CODE_TVM]: getTvmMaximumConnectorScrewLength,
  [CONNECTOR_FAMILY_CODE_GAP]: getGapMaximumConnectorScrewLength,
  [CONNECTOR_FAMILY_CODE_SNAP]: getSnapMaximumConnectorScrewLength,
  [CONNECTOR_FAMILY_CODE_KKT_COLOR]: getKktColorMaximumConnectorScrewLength,
  [CONNECTOR_FAMILY_CODE_KKT_A4_AISI316]: getKktA4Aisi316MaximumConnectorScrewLength,
};

export const getMaximumConnectorScrewLength: MaximumConnectorScrewLengthCalculator = (input) => {
  if (!input.connectorFamilyCode) {
    return undefined;
  }

  return maximumConnectorScrewLengthCalculators[input.connectorFamilyCode]?.(input);
};
